import {useContext, useState, useCallback, useEffect, useRef} from 'react';
import * as Sentry from '@sentry/react';
import {AuthContext} from "../contexts/AuthContext";
import { isPrerendering } from '../utils/prerendering';

const useAPI = () => {
    const { user } = useContext(AuthContext)
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const tokenRef = useRef(user?.token);
    
    // Update token ref when it actually changes
    useEffect(() => {
        if (tokenRef.current !== user?.token) {
            tokenRef.current = user?.token;
        }
    }, [user?.token]);

    const request = useCallback(async (url, headers, method, body=null, errorResponseOverride=null) => {
        let json;
        setLoading(true);
        setError(null);
        try {
            const defaultHeaders = { 'Content-Type': 'application/json' };
            const currentToken = tokenRef.current;
            if (currentToken) {
                defaultHeaders['Authorization'] = `Bearer ${currentToken}`
            }
            
            const fetchParams = {
                method,
                headers: { ...defaultHeaders, ...headers}
            }

            if (body) {
                fetchParams['body'] = JSON.stringify(body)
            }

            const res = await fetch(url, fetchParams);
            let parsed = {};
            try {
                const text = await res.text();
                parsed = text ? JSON.parse(text) : {};
            } catch (parseErr) {
                // Do not rewrite empty/unparseable 404 bodies to "User not found".
                // That string is reserved for a deleted-account settings response
                // and is handled in AuthContext — a missing watchlist is not a deleted user.
                parsed = { error: `HTTP ${res.status}` };
            }
            json = parsed;

            if (res.ok) {
                setData(json);
            } else {
                const error = json.Message || json.errors || json.error
                console.log('API Error Response:', { status: res.status, json, extractedError: error });
                setData(errorResponseOverride || {});
                setError(error)

                if (json.code === 'impersonation_read_only') {
                    console.warn('[useApi] Blocked write during impersonation (read-only session)');
                }
            }
        } catch (err) {
            // Provide user-friendly error messages for common network errors
            let errorMessage = 'An unexpected error occurred';
            
            if (err instanceof TypeError && err.message === 'Failed to fetch') {
                errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
            } else if (err.message) {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            
            console.error('API Request Error:', err);
            if (process.env.NODE_ENV === 'production' && !isPrerendering()) {
                Sentry.captureException(err, { extra: { url, method } });
            }
            setError(errorMessage);
            // Always return a defined object to satisfy React Query expectations
            json = errorResponseOverride || {};
        }
        setLoading(false);

        return json
    }, []); // No dependencies needed since we use ref

    return { data, error, loading, request };
};

export default useAPI;